import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

  resetKey!: string;
  firstTimeLogged = false;
  ready = false;

  remaining = 600;
  timerExpired = false;
  timer!: ReturnType<typeof setInterval>;

  hidePassword = true;
  hideConfirm = true;

  loading = false;
  apiError: string | null = null;
  success: string | null = null;

  requireOtp = false;

  passwordForm = this.fb.group({
    password: ['', [Validators.required, passwordStrength]],
    confirm: ['', Validators.required]
  }, { validators: match });

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private api: ApiService,
    private toast: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.resetKey = params['key'];
      if (!this.resetKey) {
        this.toast.error('Invalid reset link');
        return;
      }
      this.validateKey();
    });
  }

  validateKey() {
    this.api.fetchLoginStatus(`key=${this.resetKey}`).subscribe({
      next: res => {
        const data = res;
        if (data?.used==true) {
          this.timerExpired = true;
          this.toast.error('Reset link expired');
          this.router.navigate(['/login']);
          return;
        }

        this.firstTimeLogged = data?.firstTimeLogged === true;
        this.ready = true;
        this.startTimer();
      },
      error: () => {
        this.timerExpired = true;
        this.toast.error('Invalid or expired reset link');
      }
    });
  }

  startTimer() {
    this.timer = setInterval(() => {
      this.remaining--;
      if (this.remaining <= 0) {
        this.timerExpired = true;
        clearInterval(this.timer);
      }
    }, 1000);
  }

  submit() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.apiError = null;

    this.api.setnewPassword({
      key: this.resetKey,
      newPassword: this.passwordForm.value.password,
      otp: this.passwordForm.value.otp
    }).subscribe({
      next: () => {
        this.success = 'Password updated successfully';
        this.toast.success(this.success);
        this.loading = false;
        this.router.navigate(['/login']);
      },
      error: err => {
        this.apiError = err?.error?.message || 'Reset failed';
        this.loading = false;
      }
    });
  }

  goLogin() {
    this.router.navigate(['/login']);
  }

  get formattedTime() {
    const m = Math.floor(this.remaining / 60);
    const s = this.remaining % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  }

  get passwordValue() {
    return this.passwordForm.get('password')?.value || '';
  }

  get checks() {
    const v = this.passwordValue;
    return {
      length: v.length >= 8,
      upper: /[A-Z]/.test(v),
      lower: /[a-z]/.test(v),
      number: /[0-9]/.test(v),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(v)
    };
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }
}

/* ------------ Validators ------------ */

function passwordStrength(c: AbstractControl) {
  const v = c.value || '';
  return v.length >= 8 &&
    /[A-Z]/.test(v) &&
    /[a-z]/.test(v) &&
    /[0-9]/.test(v) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(v)
    ? null : { passwordStrength: true };
}

function match(group: AbstractControl) {
  const password = group.get('password');
  const confirm = group.get('confirm');

  if (!password || !confirm) return null;

  if (password.value !== confirm.value) {
    confirm.setErrors({ mismatch: true });
    return { mismatch: true };
  } else {
    confirm.setErrors(null);
    return null;
  }
}
